import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  Modal,
  Dimensions,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { AlertCircle, Check, ChevronDown, ChevronUp, X } from './../icons';
import { formFieldInStack } from './formFieldStackContext';
import { FormSearchField } from './FormSearchField';
import {
  ViewOnlyFieldValue,
  resolveIsViewOnlyPresentation,
} from './viewOnlyFieldUtils';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { elevation } from '../../theme/elevation';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight, fontWeight } from '../../theme/typography';
import {
  acquireWebOverlayScrollLock,
  releaseWebOverlayScrollLock,
} from '../../utils/webOverlayScrollLock';

import type { CSSProperties, ReactNode } from 'react';

/** Below or equal: no list filter field unless the caller sets `searchable` to `true`. */
const OPTION_COUNT_SEARCH_THRESHOLD = 5;

/** Web multiselect closed trigger: show up to this many labels as chips; remainder as “+N”. */
const WEB_MULTI_CHIP_MAX_VISIBLE = 2;

/**
 * - `searchable === true` — always show the list search field
 * - `searchable === false` — never show
 * - `searchable` omitted / `undefined` — show only when `optionCount` exceeds {@link OPTION_COUNT_SEARCH_THRESHOLD}
 */
export function resolveDropdownSearchable(
  searchable: boolean | undefined,
  optionCount: number,
): boolean {
  if (searchable === false) {
    return false;
  }
  if (searchable === true) {
    return true;
  }
  return optionCount > OPTION_COUNT_SEARCH_THRESHOLD;
}

export function filterDropdownOptionsByQuery(
  options: string[],
  query: string,
  optionSubtitles?: Record<string, string>,
): string[] {
  const t = query.trim().toLowerCase();
  if (t.length === 0) {
    return options;
  }
  return options.filter(o => {
    if (o.toLowerCase().includes(t)) {
      return true;
    }
    const sub = optionSubtitles?.[o];
    return (
      sub != null && sub.trim().length > 0 && sub.toLowerCase().includes(t)
    );
  });
}

const CHAR_W_LABEL = 7.3;
const CHAR_W_SUB = 6.4;

export function estimateDropdownContentWidth(
  options: string[],
  optionSubtitles: Record<string, string> | undefined,
): number {
  let w = spacing.formDropdownMenuMinWidth;
  for (const o of options) {
    const sub = optionSubtitles?.[o] ?? '';
    const rowW =
      Math.max(o.length * CHAR_W_LABEL, sub.length * CHAR_W_SUB) +
      spacing.formDropdownEstimateHorizontalPad;
    w = Math.max(w, rowW);
  }
  return Math.min(spacing.formDropdownMenuMaxEstimatedWidth, w);
}

export function resolveWebMenuLayout(
  rect: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
  },
  options: string[],
  optionSubtitles: Record<string, string> | undefined,
  scrollX: number,
  scrollY: number,
  viewportW: number,
): { top: number; left: number; width: number } {
  const margin = spacing.formDropdownViewportMargin;
  const maxW = Math.max(margin, viewportW - margin * 2);
  const triggerW = rect.width;
  const est = estimateDropdownContentWidth(options, optionSubtitles);
  const width = Math.min(
    maxW,
    Math.max(triggerW, spacing.formDropdownMenuMinWidth, est),
  );
  let left = rect.left + scrollX;
  if (left + width > viewportW - margin) {
    left = Math.max(margin, viewportW - margin - width);
  }
  if (left < margin) {
    left = margin;
  }
  return {
    top: rect.bottom + scrollY + spacing.formDropdownMenuOffsetY,
    left,
    width,
  };
}

/**
 * Native modal menu: same width as the trigger, pinned to its left edge.
 * Growing the menu to the longest label and then sliding it left to fit the
 * window covers the request sidebar (workload type on iPad).
 */
export function layoutNativeDropdownMenu(args: {
  x: number;
  y: number;
  width: number;
  height: number;
  itemCount: number;
  screenWidth: number;
  screenHeight: number;
}): { top: number; left: number; width: number } {
  const edge = spacing.screenPaddingHorizontal;
  const dropdownHeight = Math.min(
    Math.max(args.itemCount, 1) * spacing.formDropdownOptionMinHeight,
    spacing.formDropdownMenuMaxHeight,
  );

  const left = Math.max(edge, args.x);
  const width = Math.max(
    0,
    Math.min(args.width, args.screenWidth - left - edge),
  );

  let top = args.y + args.height + spacing.xs;
  if (top + dropdownHeight > args.screenHeight - edge) {
    top = args.y - dropdownHeight - spacing.xs;
  }
  top = Math.max(edge, top);

  return { top, left, width };
}

/** Closed-trigger chevron: Lucide stroke follows token; on web also set `style.color` for SVG `currentColor`. */
function closedDropdownChevronProps(disabled: boolean): {
  size: number;
  color: string;
  style?: ViewStyle;
} {
  const color = disabled ? colors.textFormDisabled : colors.textSecondary;
  return {
    size: spacing.formDropdownChevronSize,
    color,
    ...(Platform.OS === 'web' ? { style: { color } as ViewStyle } : {}),
  };
}

// Lightweight modal dropdown that positions near the trigger
const MobileDropdown: React.FC<{
  data: Array<{ label: string; value: string; subtitle?: string }>;
  value: string;
  onChange: (item: { value: string }) => void;
  placeholder?: string;
  disable?: boolean;
  readOnly?: boolean;
  style?: StyleProp<ViewStyle>;
  selectedTextStyle?: StyleProp<TextStyle>;
  placeholderStyle?: StyleProp<TextStyle>;
  /** Values can wrap to two lines; trigger grows (matches web two-line state). */
  textLines?: 1 | 2;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchAccessibilityLabel: string;
  allowClear?: boolean;
  onClear?: () => void;
  /** Second line in closed trigger (e.g. subtitle under selected label) when `textLines` is 2. */
  triggerSubline?: string;
  /** Optional icon before each option (e.g. per Project scope workstream). */
  renderOptionIcon?: (value: string) => ReactNode;
}> = ({
  data,
  value,
  onChange,
  placeholder,
  disable,
  readOnly = false,
  style,
  selectedTextStyle,
  placeholderStyle,
  textLines = 1,
  searchable = true,
  searchPlaceholder = 'Type to search',
  searchAccessibilityLabel,
  allowClear = false,
  onClear,
  triggerSubline,
  renderOptionIcon,
}) => {
  const canClear = Boolean(
    allowClear &&
      onClear &&
      !disable &&
      typeof value === 'string' &&
      value.trim().length > 0,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [buttonLayout, setButtonLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const buttonRef = useRef<View | null>(null);

  const filteredData = useMemo(() => {
    if (!searchable) {
      return data;
    }
    const t = searchQuery.trim().toLowerCase();
    if (t.length === 0) {
      return data;
    }
    return data.filter(item => {
      if (item.label.toLowerCase().includes(t)) {
        return true;
      }
      return (
        item.subtitle != null &&
        item.subtitle.length > 0 &&
        item.subtitle.toLowerCase().includes(t)
      );
    });
  }, [data, searchQuery, searchable]);

  const handlePress = () => {
    if (disable) return;

    // Open immediately so the Modal is testable without native measureInWindow.
    // The dropdown content is hidden (opacity: 0) until positioning completes,
    // so there is no visual flash on native.
    setSearchQuery('');
    setIsOpen(true);

    buttonRef.current?.measureInWindow((x, y, width, height) => {
      const screenHeight = Dimensions.get('window').height;
      const screenWidth = Dimensions.get('window').width;
      const layout = layoutNativeDropdownMenu({
        x,
        y,
        width,
        height,
        itemCount: data.length,
        screenWidth,
        screenHeight,
      });
      setButtonLayout({
        x: layout.left,
        y: layout.top,
        width: layout.width,
        height: 0,
      });
    });
  };

  const isTwoLineTrigger = textLines === 2;
  return (
    <>
      <View
        ref={buttonRef}
        testID="dropdown-field-surface"
        style={[
          styles.mobileDropdownButton,
          isTwoLineTrigger && styles.mobileDropdownButtonTwoLine,
          style,
        ]}
      >
        <TouchableOpacity
          style={styles.nativeDropdownMainTap}
          onPress={handlePress}
          disabled={disable}
          testID="dropdown-trigger"
        >
          <View style={styles.triggerTextClip}>
            {textLines === 2 &&
            value &&
            triggerSubline != null &&
            triggerSubline.length > 0 ? (
              <View>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={[
                    selectedTextStyle,
                    disable && !readOnly && styles.nativeDropdownTextDisabled,
                  ]}
                >
                  {value}
                </Text>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={styles.dropdownTriggerSubline}
                >
                  {triggerSubline}
                </Text>
              </View>
            ) : (
              <Text
                numberOfLines={textLines}
                ellipsizeMode="tail"
                style={[
                  value ? selectedTextStyle : placeholderStyle,
                  disable && !readOnly && styles.nativeDropdownTextDisabled,
                ]}
              >
                {value || placeholder}
              </Text>
            )}
          </View>
        </TouchableOpacity>
        {canClear && (
          <Pressable
            onPress={() => {
              onClear?.();
            }}
            style={styles.dropdownClearButton}
            hitSlop={spacing.sm}
            testID="dropdown-clear"
            accessibilityLabel="Clear selection"
            accessibilityRole="button"
            accessibilityHint="Removes the current value"
          >
            <X size={14} color={colors.textSecondary} />
          </Pressable>
        )}
        <View style={styles.mobileDropdownChevronCell} pointerEvents="box-none">
          <ChevronDown {...closedDropdownChevronProps(Boolean(disable))} />
        </View>
      </View>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setIsOpen(false);
          setButtonLayout(null);
          setSearchQuery('');
        }}
      >
        <TouchableWithoutFeedback
          onPress={() => {
            setIsOpen(false);
            setButtonLayout(null);
            setSearchQuery('');
          }}
        >
          <View style={styles.modalOverlay}>
            <View
              onStartShouldSetResponder={() => true}
              style={[
                styles.modalDropdownContainer,
                buttonLayout
                  ? {
                      top: buttonLayout.y,
                      left: buttonLayout.x,
                      width: buttonLayout.width,
                    }
                  : { opacity: 0 },
              ]}
            >
              {searchable ? (
                <View style={styles.dropdownMenuSearchShell}>
                  <FormSearchField
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder={searchPlaceholder}
                    accessibilityLabel={searchAccessibilityLabel}
                  />
                </View>
              ) : null}
              <ScrollView
                style={[
                  styles.modalDropdownScroll,
                  searchable && styles.modalDropdownScrollWithSearch,
                ]}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {filteredData.length === 0 ? (
                  <View
                    style={styles.modalDropdownEmpty}
                    accessibilityRole="text"
                    accessibilityLabel="No matching options"
                  >
                    <Text style={styles.modalDropdownEmptyText}>
                      No matching options
                    </Text>
                  </View>
                ) : (
                  filteredData.map((item, index) => {
                    const rowIcon =
                      renderOptionIcon != null
                        ? renderOptionIcon(item.value)
                        : null;
                    return (
                      <TouchableOpacity
                        key={`${item.value}-${String(index)}`}
                        testID={`dropdown-option-${item.value}`}
                        style={[
                          styles.modalDropdownItem,
                          item.subtitle != null && item.subtitle.length > 0
                            ? {
                                minHeight:
                                  spacing.formDropdownOptionMinHeightTwoLine,
                              }
                            : null,
                          value === item.value &&
                            styles.modalDropdownItemSelected,
                        ]}
                        onPress={() => {
                          onChange(item);
                          setIsOpen(false);
                          setButtonLayout(null);
                          setSearchQuery('');
                        }}
                      >
                        <View style={styles.modalDropdownItemInner}>
                          {rowIcon != null ? (
                            <View style={styles.modalOptionIconCell}>
                              {rowIcon}
                            </View>
                          ) : null}
                          <View style={styles.modalDropdownTextBlock}>
                            <Text
                              style={[
                                styles.modalDropdownItemText,
                                value === item.value &&
                                  styles.modalDropdownItemTextSelected,
                              ]}
                            >
                              {item.label}
                            </Text>
                            {item.subtitle != null &&
                            item.subtitle.length > 0 ? (
                              <Text
                                style={styles.modalDropdownItemSubtext}
                                numberOfLines={2}
                              >
                                {item.subtitle}
                              </Text>
                            ) : null}
                          </View>
                          {value === item.value ? (
                            <View
                              style={styles.modalSingleSelectCheckCell}
                              accessibilityElementsHidden
                              importantForAccessibility="no"
                            >
                              <Check size={16} color={colors.primary} />
                            </View>
                          ) : null}
                        </View>
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

/** Native: multi-select; trigger shows joined labels; list stays open until tap outside. */
const MobileMultiSelectDropdown: React.FC<{
  data: Array<{ label: string; value: string; subtitle?: string }>;
  value: string[];
  orderKeys: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disable?: boolean;
  readOnly?: boolean;
  style?: StyleProp<ViewStyle>;
  selectedTextStyle?: StyleProp<TextStyle>;
  placeholderStyle?: StyleProp<TextStyle>;
  textLines?: 1 | 2;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchAccessibilityLabel: string;
  allowClear?: boolean;
  onClear?: () => void;
  renderOptionIcon?: (value: string) => ReactNode;
}> = ({
  data,
  value,
  orderKeys,
  onChange,
  placeholder,
  disable,
  readOnly = false,
  style,
  selectedTextStyle,
  placeholderStyle,
  textLines = 1,
  searchable = true,
  searchPlaceholder = 'Type to search',
  searchAccessibilityLabel,
  allowClear = false,
  onClear,
  renderOptionIcon,
}) => {
  const canClear = Boolean(
    allowClear && onClear && !disable && value.length > 0,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [buttonLayout, setButtonLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const buttonRef = useRef<View | null>(null);
  const selected = useMemo(() => new Set(value), [value]);

  const filteredData = useMemo(() => {
    if (!searchable) {
      return data;
    }
    const t = searchQuery.trim().toLowerCase();
    if (t.length === 0) {
      return data;
    }
    return data.filter(item => {
      if (item.label.toLowerCase().includes(t)) {
        return true;
      }
      return (
        item.subtitle != null &&
        item.subtitle.length > 0 &&
        item.subtitle.toLowerCase().includes(t)
      );
    });
  }, [data, searchQuery, searchable]);

  // Render each pick through its display label (`data` carries the optionLabels
  // override); falls back to the canonical value when unmapped.
  const displayTrigger = useMemo(
    () =>
      value.length > 0
        ? value.map(v => data.find(d => d.value === v)?.label ?? v).join(', ')
        : '',
    [value, data],
  );

  const handlePress = () => {
    if (disable) {
      return;
    }
    setSearchQuery('');
    setIsOpen(true);
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      const screenHeight = Dimensions.get('window').height;
      const screenWidth = Dimensions.get('window').width;
      const layout = layoutNativeDropdownMenu({
        x,
        y,
        width,
        height,
        itemCount: data.length,
        screenWidth,
        screenHeight,
      });
      setButtonLayout({
        x: layout.left,
        y: layout.top,
        width: layout.width,
        height: 0,
      });
    });
  };

  const isTwoLineTrigger = textLines === 2;
  return (
    <>
      <View
        ref={buttonRef}
        testID="dropdown-field-surface"
        style={[
          styles.mobileDropdownButton,
          isTwoLineTrigger && styles.mobileDropdownButtonTwoLine,
          style,
        ]}
      >
        <TouchableOpacity
          style={styles.nativeDropdownMainTap}
          onPress={handlePress}
          disabled={disable}
          testID="dropdown-trigger"
        >
          <View style={styles.triggerTextClip}>
            <Text
              numberOfLines={textLines}
              ellipsizeMode="tail"
              style={[
                displayTrigger ? selectedTextStyle : placeholderStyle,
                disable && !readOnly && styles.nativeDropdownTextDisabled,
              ]}
            >
              {displayTrigger || placeholder}
            </Text>
          </View>
        </TouchableOpacity>
        {canClear && (
          <Pressable
            onPress={() => {
              onClear?.();
            }}
            style={styles.dropdownClearButton}
            hitSlop={spacing.sm}
            testID="dropdown-clear"
            accessibilityLabel="Clear selection"
            accessibilityRole="button"
          >
            <X size={14} color={colors.textSecondary} />
          </Pressable>
        )}
        <View style={styles.mobileDropdownChevronCell} pointerEvents="box-none">
          <ChevronDown {...closedDropdownChevronProps(Boolean(disable))} />
        </View>
      </View>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setIsOpen(false);
          setButtonLayout(null);
          setSearchQuery('');
        }}
      >
        <TouchableWithoutFeedback
          onPress={() => {
            setIsOpen(false);
            setButtonLayout(null);
            setSearchQuery('');
          }}
        >
          <View style={styles.modalOverlay}>
            <View
              onStartShouldSetResponder={() => true}
              style={[
                styles.modalDropdownContainer,
                buttonLayout
                  ? {
                      top: buttonLayout.y,
                      left: buttonLayout.x,
                      width: buttonLayout.width,
                    }
                  : { opacity: 0 },
              ]}
            >
              {searchable ? (
                <View style={styles.dropdownMenuSearchShell}>
                  <FormSearchField
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder={searchPlaceholder}
                    accessibilityLabel={searchAccessibilityLabel}
                  />
                </View>
              ) : null}
              <ScrollView
                style={[
                  styles.modalDropdownScroll,
                  searchable && styles.modalDropdownScrollWithSearch,
                ]}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {filteredData.length === 0 ? (
                  <View
                    style={styles.modalDropdownEmpty}
                    accessibilityRole="text"
                  >
                    <Text style={styles.modalDropdownEmptyText}>
                      No matching options
                    </Text>
                  </View>
                ) : (
                  filteredData.map((item, index) => {
                    const rowIcon =
                      renderOptionIcon != null
                        ? renderOptionIcon(item.value)
                        : null;
                    const isOn = selected.has(item.value);
                    return (
                      <TouchableOpacity
                        key={`${item.value}-${String(index)}`}
                        testID={`dropdown-option-${item.value}`}
                        style={[
                          styles.modalDropdownItem,
                          item.subtitle != null && item.subtitle.length > 0
                            ? {
                                minHeight:
                                  spacing.formDropdownOptionMinHeightTwoLine,
                              }
                            : null,
                          isOn && styles.modalDropdownItemSelected,
                        ]}
                        onPress={() => {
                          const next = new Set(value);
                          if (next.has(item.value)) {
                            next.delete(item.value);
                          } else {
                            next.add(item.value);
                          }
                          onChange(orderKeys.filter(k => next.has(k)));
                        }}
                      >
                        <View style={styles.modalDropdownItemInnerMulti}>
                          {rowIcon != null ? (
                            <View style={styles.modalOptionIconCell}>
                              {rowIcon}
                            </View>
                          ) : null}
                          <View style={styles.modalDropdownTextBlock}>
                            <Text
                              style={[
                                styles.modalDropdownItemText,
                                isOn && styles.modalDropdownItemTextSelected,
                              ]}
                            >
                              {item.label}
                            </Text>
                            {item.subtitle != null &&
                            item.subtitle.length > 0 ? (
                              <Text
                                style={styles.modalDropdownItemSubtext}
                                numberOfLines={2}
                              >
                                {item.subtitle}
                              </Text>
                            ) : null}
                          </View>
                          <View
                            style={[
                              styles.multiSelectCheck,
                              isOn && styles.multiSelectCheckOn,
                            ]}
                            accessibilityElementsHidden
                            importantForAccessibility="no"
                          >
                            {isOn ? (
                              <Check size={12} color={colors.textLight} />
                            ) : null}
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

// Conditional import for react-dom
let createPortal: ((ui: ReactNode, container: Element) => ReactNode) | null =
  null;
if (Platform.OS === 'web') {
  try {
    // @ts-expect-error -- optional peer; tests may omit react-dom
    createPortal = require('react-dom').createPortal as (
      ui: ReactNode,
      container: Element,
    ) => ReactNode;
  } catch (e) {
    console.warn('react-dom not available');
  }
}

interface FormDropdownProps {
  label: string;
  /** Single: `string`. Multi: `string[]` when `multiple` is set. */
  value: string | string[];
  onPress?: () => void;
  options?: string[];
  /** Single: `string`. Multi: `string[]` when `multiple` is set. */
  onSelect?: (value: string | string[]) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnlyMode?: boolean;
  /** When false with `disabled`, keeps legacy read-only chrome instead of view-only text. */
  editable?: boolean;
  error?: string;
  helperText?: string;
  containerStyle?: StyleProp<ViewStyle>;
  dropdownStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  /**
   * Wraps the label (when shown). Use for side-by-side fields: same `minHeight` / alignment so
   * triggers line up (e.g. Project Details Team: sponsors + retrofit row).
   */
  labelBlockStyle?: StyleProp<ViewStyle>;
  /**
   * `2`: allow up to two lines for the value/placeholder in the closed trigger (taller control).
   * `1` (default): single-line trigger (compact).
   */
  triggerTextLines?: 1 | 2;
  /**
   * List filter: `true` = always, `false` = never, omit = automatic (on only if there are more than
   * five options).
   */
  searchable?: boolean;
  /** Shown in the list filter field (web + native). */
  searchPlaceholder?: string;
  /**
   * Optional second line in each list row (e.g. “2 sites” under a customer name).
   * Keys must match entries in `options` exactly.
   */
  optionSubtitles?: Record<string, string>;
  /**
   * Display-only label override per option VALUE. The stored/selected value stays
   * the canonical option string; only the rendered text changes. Used where the
   * canonical value is verbose because it carries meaning elsewhere (e.g. the Smart
   * IT workload picks gate the NVL config fields, so their values must not change).
   * Unmapped options render as-is.
   */
  optionLabels?: Record<string, string>;
  /**
   * When true, show a clear (×) control when a value is set so the user can reset
   * without opening the list (calls `onSelect` with `''`).
   */
  allowClear?: boolean;
  /**
   * Optional small icon before each list row and inline with label (web + native modal list).
   */
  renderOptionIcon?: (option: string) => ReactNode;
  /**
   * When set, the control keeps a **dropdown** trigger; the open list supports multiple values
   * (checkboxes). `value` must be `string[]` and `onSelect` is called with `string[]`.
   */
  multiple?: boolean;
  testID?: string;
}

export const FormDropdown: React.FC<FormDropdownProps> = ({
  label,
  value,
  onPress,
  options = [],
  onSelect,
  placeholder,
  required = false,
  disabled = false,
  readOnlyMode = false,
  editable = true,
  error,
  helperText,
  containerStyle,
  dropdownStyle,
  style,
  labelBlockStyle,
  triggerTextLines = 1,
  searchable: searchableProp,
  searchPlaceholder: searchPlaceholderProp = 'Type to search',
  optionSubtitles,
  optionLabels,
  allowClear = false,
  renderOptionIcon,
  multiple = false,
  testID,
}) => {
  const isMulti = multiple === true;
  const vList = useMemo((): string[] => {
    if (!isMulti) {
      return [];
    }
    return Array.isArray(value) ? (value as string[]) : [];
  }, [isMulti, value]);
  const vStr: string =
    !isMulti && typeof value === 'string' ? (value as string) : '';
  const selectedSet = useMemo(
    () => (isMulti ? new Set(vList) : new Set<string>()),
    [isMulti, vList],
  );
  const isTwoLineTrigger = triggerTextLines === 2;
  const searchable = useMemo(
    () => resolveDropdownSearchable(searchableProp, options.length),
    [searchableProp, options.length],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef<View>(null);

  const filteredOptions = useMemo(
    () =>
      searchable
        ? filterDropdownOptionsByQuery(options, searchQuery, optionSubtitles)
        : options,
    [options, searchQuery, searchable, optionSubtitles],
  );

  const listFilterA11yLabel = useMemo(() => {
    const n = (
      label && label.trim().length > 0 ? label : placeholder || 'options'
    ).trim();
    return `Filter ${n} list`;
  }, [label, placeholder]);
  const { canEditRequest } = useFormPermissions();
  const hasError = Boolean(error);
  const showPlaceholder = isMulti
    ? vList.length === 0 && Boolean(placeholder)
    : !vStr && Boolean(placeholder);

  // Determine if the dropdown should be disabled based on permissions
  const isPermissionLocked = !disabled && !readOnlyMode && !canEditRequest;
  const isDisabled = disabled || readOnlyMode || isPermissionLocked;
  const isReadOnly = readOnlyMode || isPermissionLocked;
  const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
    readOnlyMode,
    disabled,
    editable,
    canEditRequest,
  });
  const isInteractionDisabled = isDisabled && !isReadOnly;

  const canShowClear =
    allowClear &&
    Boolean(onSelect) &&
    !isDisabled &&
    (isMulti
      ? vList.length > 0
      : typeof vStr === 'string' && vStr.trim().length > 0);

  const selectedOptionSubtitle = useMemo(() => {
    if (isMulti || !isTwoLineTrigger || !vStr) {
      return '';
    }
    return (optionSubtitles?.[vStr] ?? '').trim();
  }, [isMulti, isTwoLineTrigger, vStr, optionSubtitles]);

  const showSelectedLabelAndSubtitle =
    !isMulti &&
    isTwoLineTrigger &&
    Boolean(vStr) &&
    selectedOptionSubtitle.length > 0;

  const handleClear = useCallback(() => {
    if (isMulti) {
      onSelect?.([]);
    } else {
      onSelect?.('');
    }
    setSearchQuery('');
    setIsOpen(false);
  }, [isMulti, onSelect]);

  // Web portal: menu is at least as wide as the trigger, and wide enough for the longest option row.
  const updateDropdownPosition = useCallback(() => {
    if (
      Platform.OS === 'web' &&
      dropdownRef.current &&
      typeof window !== 'undefined'
    ) {
      const element = dropdownRef.current as unknown as HTMLElement;
      const rect = element.getBoundingClientRect();
      const { top, left, width } = resolveWebMenuLayout(
        rect,
        options,
        optionSubtitles,
        window.scrollX,
        window.scrollY,
        window.innerWidth,
      );
      setDropdownPosition({ top, left, width });
    }
  }, [options, optionSubtitles]);

  // Close dropdown when clicking outside (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen && typeof document !== 'undefined') {
      const handleClickOutside = (event: Event) => {
        const target = event.target;
        if (!(target instanceof Node)) {
          return;
        }
        const portalDropdown = document.querySelector(
          '[data-dropdown-portal="true"]',
        );
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains?.(target) &&
          (!portalDropdown || !portalDropdown.contains(target))
        ) {
          setIsOpen(false);
        }
      };

      const handleScroll = () => {
        updateDropdownPosition();
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Web: keep nested / page scroll from moving behind the portaled menu (incl. RN Web ScrollView).
  useEffect(() => {
    if (Platform.OS !== 'web' || !isOpen) {
      return undefined;
    }
    acquireWebOverlayScrollLock();
    return () => {
      releaseWebOverlayScrollLock();
    };
  }, [isOpen]);

  const handlePress = () => {
    if (isDisabled) return;

    if (options.length > 0 && onSelect) {
      if (Platform.OS === 'web') {
        if (!isOpen) {
          setSearchQuery('');
          updateDropdownPosition();
        }
        setIsOpen(prev => {
          if (prev) {
            return false;
          }
          return true;
        });
      } else {
        setIsOpen(prev => !prev);
      }
    } else if (onPress) {
      onPress();
    }
  };

  const applyMultiSelection = (opt: string) => {
    if (!onSelect) {
      return;
    }
    const s = new Set(vList);
    if (s.has(opt)) {
      s.delete(opt);
    } else {
      s.add(opt);
    }
    onSelect?.(options.filter(k => s.has(k)));
  };

  const removeMultiOption = useCallback(
    (opt: string) => {
      if (!onSelect || isDisabled || !isMulti) {
        return;
      }
      onSelect(vList.filter(v => v !== opt));
    },
    [onSelect, isDisabled, isMulti, vList],
  );

  const handleSelectAllFiltered = useCallback(() => {
    if (!onSelect || !isMulti) {
      return;
    }
    const filt = filteredOptions;
    if (filt.length === 0) {
      return;
    }
    const allFilteredSelected = filt.every(o => selectedSet.has(o));
    if (allFilteredSelected) {
      const drop = new Set(filt);
      onSelect(vList.filter(v => !drop.has(v)));
    } else {
      const union = new Set(vList);
      filt.forEach(o => {
        union.add(o);
      });
      onSelect(options.filter(k => union.has(k)));
    }
  }, [onSelect, isMulti, filteredOptions, selectedSet, vList, options]);

  const webMultiChipLabels = useMemo(() => {
    if (!isMulti || Platform.OS !== 'web') {
      return [];
    }
    if (vList.length <= WEB_MULTI_CHIP_MAX_VISIBLE) {
      return vList;
    }
    return vList.slice(0, WEB_MULTI_CHIP_MAX_VISIBLE);
  }, [isMulti, vList]);

  const webMultiChipOverflowCount = useMemo(() => {
    if (!isMulti || Platform.OS !== 'web') {
      return 0;
    }
    return Math.max(0, vList.length - webMultiChipLabels.length);
  }, [isMulti, vList, webMultiChipLabels.length]);

  const handleOptionSelect = (selectedValue: string) => {
    onSelect?.(selectedValue);
    setSearchQuery('');
    setIsOpen(false);
  };

  const dropdownItems = useMemo(
    () =>
      options.map(option => ({
        // Display-only override; `value` stays canonical.
        label: optionLabels?.[option] ?? option,
        value: option,
        subtitle: optionSubtitles?.[option],
      })),
    [options, optionSubtitles, optionLabels],
  );

  const showLabel = label.trim().length > 0;
  const formGroupStyle = [
    styles.formGroup,
    containerStyle,
    style,
    formFieldInStack,
  ];

  if (isViewOnlyPresentation) {
    const viewValue = isMulti ? vList : vStr;
    return (
      <ViewOnlyFieldValue
        label={label}
        value={viewValue}
        hideLabel={!showLabel}
        testID={testID ?? 'dropdown-container'}
        valueTestID="view-only-field-value"
        containerStyle={formGroupStyle}
        error={error}
        helperText={helperText}
      />
    );
  }

  // If mobile, use custom dropdown that handles z-index properly
  if (Platform.OS !== 'web' && options.length > 0 && onSelect) {
    if (isMulti) {
      return (
        <View style={formGroupStyle} testID={testID ?? 'dropdown-container'}>
          {showLabel ? (
            <View style={[{ width: '100%' }, labelBlockStyle]}>
              <Text style={styles.label}>
                {label}
                {required && <Text style={styles.required}> *</Text>}
              </Text>
            </View>
          ) : null}
          <MobileMultiSelectDropdown
            data={dropdownItems}
            value={vList}
            orderKeys={options}
            onChange={arr => {
              onSelect?.(arr);
            }}
            allowClear={allowClear}
            onClear={allowClear && onSelect ? handleClear : undefined}
            textLines={triggerTextLines}
            placeholder={placeholder || `Select ${label.toLowerCase()}`}
            disable={isDisabled}
            readOnly={isReadOnly}
            searchable={searchable}
            searchPlaceholder={searchPlaceholderProp}
            searchAccessibilityLabel={listFilterA11yLabel}
            renderOptionIcon={renderOptionIcon}
            style={[
              styles.nativeDropdown,
              isTwoLineTrigger && styles.nativeDropdownTwoLine,
              hasError && styles.nativeDropdownError,
              isDisabled && !isReadOnly && styles.nativeDropdownDisabled,
              isReadOnly && styles.nativeDropdownReadOnly,
              dropdownStyle,
            ]}
            selectedTextStyle={[
              styles.nativeDropdownText,
              isDisabled && !isReadOnly && styles.nativeDropdownTextDisabled,
              isReadOnly && styles.nativeDropdownTextReadOnly,
            ]}
            placeholderStyle={styles.nativeDropdownPlaceholder}
          />
          {error && (
            <View
              style={styles.errorRow}
              accessibilityRole="text"
              accessibilityLabel={error}
            >
              <AlertCircle
                size={14}
                color={colors.error}
                style={styles.errorRowIcon}
              />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}
          {helperText && !error && (
            <Text style={styles.helperText}>{helperText}</Text>
          )}
        </View>
      );
    }
    return (
      <View style={formGroupStyle} testID={testID ?? 'dropdown-container'}>
        {showLabel ? (
          <View style={[{ width: '100%' }, labelBlockStyle]}>
            <Text style={styles.label}>
              {label}
              {required && <Text style={styles.required}> *</Text>}
            </Text>
          </View>
        ) : null}
        <MobileDropdown
          data={dropdownItems}
          value={vStr}
          onChange={item => {
            onSelect?.(item.value);
          }}
          allowClear={allowClear}
          onClear={allowClear && onSelect ? handleClear : undefined}
          textLines={triggerTextLines}
          triggerSubline={
            showSelectedLabelAndSubtitle && selectedOptionSubtitle
              ? selectedOptionSubtitle
              : undefined
          }
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          disable={isDisabled}
          readOnly={isReadOnly}
          searchable={searchable}
          searchPlaceholder={searchPlaceholderProp}
          searchAccessibilityLabel={listFilterA11yLabel}
          renderOptionIcon={renderOptionIcon}
          style={[
            styles.nativeDropdown,
            isTwoLineTrigger && styles.nativeDropdownTwoLine,
            hasError && styles.nativeDropdownError,
            isDisabled && !isReadOnly && styles.nativeDropdownDisabled,
            isReadOnly && styles.nativeDropdownReadOnly,
            dropdownStyle,
          ]}
          selectedTextStyle={[
            styles.nativeDropdownText,
            isDisabled && !isReadOnly && styles.nativeDropdownTextDisabled,
            isReadOnly && styles.nativeDropdownTextReadOnly,
          ]}
          placeholderStyle={styles.nativeDropdownPlaceholder}
        />
        {error && (
          <View
            style={styles.errorRow}
            accessibilityRole="text"
            accessibilityLabel={error}
          >
            <AlertCircle
              size={14}
              color={colors.error}
              style={styles.errorRowIcon}
            />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}
        {helperText && !error && (
          <Text style={styles.helperText}>{helperText}</Text>
        )}
      </View>
    );
  }

  // Web version (existing implementation)
  const a11yName = (
    label && label.trim() ? label : placeholder || 'Option'
  ).trim();

  return (
    <View style={formGroupStyle} testID={testID ?? 'dropdown-container'}>
      {showLabel ? (
        <View style={[{ width: '100%' }, labelBlockStyle]}>
          <Text style={styles.label}>
            {label}
            {required && <Text style={styles.required}> *</Text>}
          </Text>
        </View>
      ) : null}
      <View style={styles.dropdownContainer} ref={dropdownRef}>
        <View
          testID="dropdown-field-surface"
          style={[
            styles.dropdown,
            isTwoLineTrigger && styles.dropdownTwoLine,
            isDisabled && !isReadOnly && styles.disabledDropdown,
            isReadOnly && styles.readOnlyDropdown,
            hasError && styles.errorDropdown,
            isOpen && styles.openDropdown,
            Platform.OS === 'web' &&
              isOpen &&
              !hasError &&
              styles.dropdownWebOpen,
            dropdownStyle,
          ]}
        >
          {/**
           * One row control: entire trigger (value + chevron) toggles the menu. Clear is separate
           * with stopPropagation so it does not open. Chevron was previously outside the touch target.
           */}
          <TouchableOpacity
            style={[
              styles.webTriggerRow,
              isReadOnly && styles.webTriggerRowReadOnly,
            ]}
            onPress={handlePress}
            activeOpacity={isDisabled ? 1 : 0.7}
            disabled={isInteractionDisabled}
            accessibilityLabel={`${a11yName} dropdown`}
            accessibilityHint={
              a11yName ? `Select ${a11yName.toLowerCase()}` : 'Select an option'
            }
            accessibilityRole="button"
            testID="dropdown-trigger"
            accessibilityState={{
              selected: isMulti ? vList.length > 0 : Boolean(vStr),
              disabled: isInteractionDisabled,
              expanded: isOpen,
            }}
            {...Platform.select({
              web: {
                'aria-label': `${a11yName} dropdown`,
                'aria-required': required,
                'aria-invalid': hasError,
                'aria-describedby': error
                  ? `${a11yName}-error`
                  : helperText
                  ? `${a11yName}-helper`
                  : undefined,
                role: 'combobox',
                'aria-expanded': isOpen,
                'aria-disabled': isInteractionDisabled,
                tabIndex: isInteractionDisabled ? -1 : 0,
                onKeyDown: (e: {
                  key?: string;
                  preventDefault?: () => void;
                }) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault?.();
                    handlePress();
                  }
                },
              },
            })}
          >
            <View style={styles.webTriggerMain}>
              <View
                style={[
                  styles.triggerTextClip,
                  isReadOnly && styles.triggerTextClipReadOnly,
                ]}
              >
                {isMulti ? (
                  vList.length === 0 ? (
                    <Text
                      numberOfLines={triggerTextLines}
                      ellipsizeMode="tail"
                      style={[
                        styles.dropdownText,
                        Boolean(placeholder) && styles.placeholderText,
                        isDisabled && !isReadOnly && styles.disabledText,
                        isReadOnly && styles.readOnlyText,
                      ]}
                    >
                      {placeholder || ''}
                    </Text>
                  ) : (
                    <View style={styles.webMultiChipRow}>
                      {webMultiChipLabels.map(opt => (
                        <Pressable
                          key={opt}
                          disabled={isDisabled}
                          onPress={ev => {
                            const e = ev as {
                              stopPropagation?: () => void;
                              nativeEvent?: {
                                stopImmediatePropagation?: () => void;
                              };
                            };
                            e?.stopPropagation?.();
                            e?.nativeEvent?.stopImmediatePropagation?.();
                            removeMultiOption(opt);
                          }}
                          style={({ pressed }) => [
                            styles.webMultiChip,
                            pressed &&
                              !isDisabled &&
                              styles.webMultiChipPressed,
                          ]}
                          accessibilityLabel={`Remove ${
                            optionLabels?.[opt] ?? opt
                          }`}
                          accessibilityRole="button"
                          hitSlop={spacing.xs}
                        >
                          <Text
                            style={styles.webMultiChipLabel}
                            numberOfLines={1}
                          >
                            {optionLabels?.[opt] ?? opt}
                          </Text>
                          {!isDisabled ? (
                            <X size={12} color={colors.textSecondary} />
                          ) : null}
                        </Pressable>
                      ))}
                      {webMultiChipOverflowCount > 0 ? (
                        <Text
                          style={styles.webMultiChipOverflow}
                          accessibilityLabel={`${webMultiChipOverflowCount} more selected`}
                        >
                          +{webMultiChipOverflowCount}
                        </Text>
                      ) : null}
                    </View>
                  )
                ) : showSelectedLabelAndSubtitle ? (
                  <View>
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={[
                        styles.dropdownText,
                        isDisabled && !isReadOnly && styles.disabledText,
                        isReadOnly && styles.readOnlyText,
                      ]}
                    >
                      {vStr}
                    </Text>
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={styles.dropdownTriggerSubline}
                    >
                      {selectedOptionSubtitle}
                    </Text>
                  </View>
                ) : (
                  <Text
                    numberOfLines={triggerTextLines}
                    ellipsizeMode="tail"
                    style={[
                      styles.dropdownText,
                      showPlaceholder && styles.placeholderText,
                      isDisabled && !isReadOnly && styles.disabledText,
                      isReadOnly && styles.readOnlyText,
                    ]}
                  >
                    {vStr || placeholder}
                  </Text>
                )}
              </View>
            </View>
            {canShowClear && (
              <Pressable
                onPress={ev => {
                  const e = ev as {
                    stopPropagation?: () => void;
                    nativeEvent?: { stopImmediatePropagation?: () => void };
                  };
                  e?.stopPropagation?.();
                  e?.nativeEvent?.stopImmediatePropagation?.();
                  handleClear();
                }}
                style={({ pressed }) => [
                  styles.dropdownClearButton,
                  pressed && styles.dropdownClearButtonPressed,
                ]}
                hitSlop={spacing.sm}
                testID="dropdown-clear"
                accessibilityLabel={`Clear ${a11yName} selection`}
                accessibilityRole="button"
              >
                <X size={16} color={colors.textSecondary} />
              </Pressable>
            )}
            {Platform.OS === 'web' && hasError ? (
              <View
                style={styles.dropdownTriggerErrorIcon}
                accessibilityElementsHidden
                importantForAccessibility="no"
              >
                <AlertCircle size={18} color={colors.error} />
              </View>
            ) : null}
            <View style={styles.chevronBlock} accessibilityElementsHidden>
              {isOpen ? (
                <ChevronUp {...closedDropdownChevronProps(isDisabled)} />
              ) : (
                <ChevronDown {...closedDropdownChevronProps(isDisabled)} />
              )}
            </View>
          </TouchableOpacity>
        </View>

        {/* Mobile dropdown - inline */}
        {Platform.OS !== 'web' && isOpen && options.length > 0 && (
          <View style={styles.optionsContainer}>
            <ScrollView
              style={styles.optionsList}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
            >
              {options.map((option, index) => (
                <TouchableOpacity
                  key={index}
                  testID={`dropdown-option-${option}`}
                  style={[
                    styles.option,
                    value === option && styles.selectedOption,
                  ]}
                  onPress={() => handleOptionSelect(option)}
                  accessibilityRole="button"
                  accessibilityLabel={`Select ${option}`}
                >
                  <Text
                    style={[
                      styles.optionText,
                      value === option && styles.selectedOptionText,
                    ]}
                  >
                    {optionLabels?.[option] ?? option}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}
      </View>

      {error && (
        <View
          style={styles.errorRow}
          accessibilityRole="text"
          accessibilityLabel={error}
        >
          <AlertCircle
            size={14}
            color={colors.error}
            style={styles.errorRowIcon}
          />
          <Text
            style={styles.errorText}
            {...Platform.select({
              web: { id: `${a11yName}-error` },
            })}
          >
            {error}
          </Text>
        </View>
      )}
      {helperText && !error && (
        <Text
          style={styles.helperText}
          {...Platform.select({
            web: { id: `${a11yName}-helper` },
          })}
        >
          {helperText}
        </Text>
      )}

      {/* Web dropdown - portal */}
      {Platform.OS === 'web' &&
        isOpen &&
        options.length > 0 &&
        createPortal && (
          <>
            {createPortal(
              <div
                data-dropdown-portal="true"
                style={{
                  position: 'absolute',
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                  backgroundColor: colors.surfaceDropdownMenuChrome,
                  borderRadius: spacing.formDropdownBorderRadius,
                  padding: spacing.formDropdownMenuChromePadding,
                  maxHeight: spacing.formDropdownMenuMaxHeight,
                  zIndex: 999999,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxSizing: 'border-box',
                  ...(elevation.dropdownPanel as CSSProperties),
                }}
              >
                {searchable ? (
                  <View style={styles.dropdownMenuSearchShell}>
                    <FormSearchField
                      value={searchQuery}
                      onChangeText={setSearchQuery}
                      placeholder={searchPlaceholderProp}
                      accessibilityLabel={listFilterA11yLabel}
                    />
                  </View>
                ) : null}
                <div
                  style={{
                    maxHeight: searchable
                      ? spacing.formDropdownListMaxHeightWithSearch
                      : spacing.formDropdownListMaxHeight,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: searchable ? spacing.sm : 0,
                    paddingBottom: searchable ? spacing.sm : 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.formDropdownOptionRowGap,
                    boxSizing: 'border-box',
                  }}
                >
                  {filteredOptions.length === 0 ? (
                    <div
                      style={{
                        padding: spacing.md,
                        fontSize: fontSize.formControl,
                        lineHeight: `${lineHeight.formControl}px`,
                        color: colors.textSecondary,
                      }}
                      role="status"
                    >
                      No matching options
                    </div>
                  ) : (
                    <>
                      {isMulti ? (
                        <div
                          key="dropdown-select-all"
                          data-testid="dropdown-select-all"
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: spacing.sm,
                            boxSizing: 'border-box',
                            minHeight: spacing.formDropdownOptionMinHeight,
                            padding: `${spacing.formDropdownOptionPaddingVertical}px ${spacing.formDropdownOptionPaddingHorizontal}px`,
                            borderRadius: spacing.formDropdownBorderRadius,
                            cursor: 'pointer',
                            backgroundColor: colors.backgroundForm,
                            color: colors.textPrimary,
                          }}
                          onClick={e => {
                            e.preventDefault();
                            handleSelectAllFiltered();
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor =
                              colors.surfaceHover;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor =
                              colors.backgroundForm;
                          }}
                        >
                          <div
                            style={{
                              minWidth: 0,
                              flex: 1,
                              fontSize: fontSize.formControl,
                              fontWeight: fontWeight.medium,
                              lineHeight: `${lineHeight.formControl}px`,
                            }}
                          >
                            Select all
                          </div>
                          {(() => {
                            const allFilteredSelected = filteredOptions.every(
                              o => selectedSet.has(o),
                            );
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  width: 22,
                                  height: 22,
                                  flexShrink: 0,
                                  borderRadius: spacing.xs,
                                  border: `1px solid ${
                                    allFilteredSelected
                                      ? colors.primary
                                      : colors.borderMedium
                                  }`,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: 'auto',
                                  backgroundColor: allFilteredSelected
                                    ? colors.primary
                                    : colors.backgroundForm,
                                }}
                                aria-hidden
                              >
                                {allFilteredSelected ? (
                                  <Check size={12} color={colors.textLight} />
                                ) : null}
                              </div>
                            );
                          })()}
                        </div>
                      ) : null}
                      {filteredOptions.map((option, index) => {
                        const sub = optionSubtitles?.[option];
                        const hasSub = sub != null && sub.length > 0;
                        const rowMinH = hasSub
                          ? spacing.formDropdownOptionMinHeightTwoLine
                          : spacing.formDropdownOptionMinHeight;
                        const optionIcon =
                          renderOptionIcon != null
                            ? renderOptionIcon(option)
                            : null;
                        const isSelected = isMulti
                          ? selectedSet.has(option)
                          : vStr === option;
                        return (
                          <div
                            key={`${option}-web-${String(index)}`}
                            data-testid={`dropdown-option-${option}`}
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: spacing.sm,
                              boxSizing: 'border-box',
                              minHeight: rowMinH,
                              padding: `${spacing.formDropdownOptionPaddingVertical}px ${spacing.formDropdownOptionPaddingHorizontal}px`,
                              borderRadius: spacing.formDropdownBorderRadius,
                              cursor: 'pointer',
                              backgroundColor: isSelected
                                ? colors.surfaceSelectSelected
                                : colors.backgroundForm,
                              color: colors.textPrimary,
                            }}
                            onClick={() => {
                              if (isMulti) {
                                applyMultiSelection(option);
                              } else {
                                handleOptionSelect(option);
                              }
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor =
                                  colors.surfaceHover;
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  colors.surfaceSelectSelected;
                              }
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = isSelected
                                ? colors.surfaceSelectSelected
                                : colors.backgroundForm;
                            }}
                          >
                            {optionIcon != null ? (
                              <div
                                style={{
                                  display: 'flex',
                                  paddingTop: 1,
                                  flexShrink: 0,
                                  color: isSelected
                                    ? colors.textPrimary
                                    : colors.textSecondary,
                                }}
                              >
                                {optionIcon}
                              </div>
                            ) : null}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontSize: fontSize.formControl,
                                  fontWeight: '400',
                                  lineHeight: `${lineHeight.formControl}px`,
                                  color: colors.textPrimary,
                                }}
                              >
                                {optionLabels?.[option] ?? option}
                              </div>
                              {sub != null && sub.length > 0 ? (
                                <div
                                  style={{
                                    fontSize: fontSize.formLabel,
                                    color: colors.textSecondary,
                                    fontWeight: 400,
                                    marginTop: 2,
                                    lineHeight: `${lineHeight.formLabel}px`,
                                  }}
                                >
                                  {sub}
                                </div>
                              ) : null}
                            </div>
                            {isMulti ? (
                              <div
                                style={{
                                  display: 'flex',
                                  width: 22,
                                  height: 22,
                                  flexShrink: 0,
                                  borderRadius: spacing.xs,
                                  border: `1px solid ${
                                    isSelected
                                      ? colors.primary
                                      : colors.borderMedium
                                  }`,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: 'auto',
                                  backgroundColor: isSelected
                                    ? colors.primary
                                    : colors.backgroundForm,
                                }}
                                aria-hidden
                              >
                                {isSelected ? (
                                  <Check size={12} color={colors.textLight} />
                                ) : null}
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  width: 22,
                                  height: 22,
                                  flexShrink: 0,
                                  marginLeft: 'auto',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                aria-hidden
                              >
                                {isSelected ? (
                                  <Check size={16} color={colors.primary} />
                                ) : null}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>,
              typeof document !== 'undefined' ? document.body : (null as any),
            )}
          </>
        )}
    </View>
  );
};

export interface FormDropdownTriggerShellProps {
  /**
   * Left column body — same inner layout region as {@link FormDropdown} closed trigger
   * (`webTriggerMain` / `triggerTextClip`). Chevron matches real selects.
   */
  children: ReactNode;
  onPress: () => void;
  disabled?: boolean;
  /** Same 2px critical stroke as dropdown error state */
  error?: boolean;
  testID?: string;
  /** Applied to the outer `dropdownContainer` (e.g. step-level vertical spacing). */
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

/**
 * Bordered closed-trigger shell shared with {@link FormDropdown} (hover/focus ring, typography row,
 * chevron). Use when the menu is a separate modal (`TeamMemberSelector`, etc.) instead of the portaled list.
 *
 * For empty-state copy inside the trigger, render `Text` with {@link formDropdownTriggerPlaceholderLabelStyle}
 * (and {@link colors.textFormDisabled} when `disabled`) so web matches {@link FormInput} placeholders.
 */
export const FormDropdownTriggerShell: React.FC<
  FormDropdownTriggerShellProps
> = ({
  children,
  onPress,
  disabled = false,
  error = false,
  testID,
  style,
  accessibilityLabel,
}) => {
  const hasError = Boolean(error);
  const isDisabled = disabled;

  const handlePress = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onPress();
  }, [isDisabled, onPress]);

  const a11yBase = (accessibilityLabel ?? 'Selection').trim();

  return (
    <View style={[styles.dropdownContainer, style]}>
      <View
        testID="dropdown-field-surface"
        style={[
          styles.dropdown,
          styles.dropdownModalTrigger,
          isDisabled && styles.disabledDropdown,
          hasError && styles.errorDropdown,
        ]}
      >
        <TouchableOpacity
          style={styles.webTriggerRow}
          onPress={handlePress}
          activeOpacity={isDisabled ? 1 : 0.7}
          disabled={isDisabled}
          accessibilityLabel={a11yBase}
          accessibilityHint={`Open ${a11yBase.toLowerCase()}`}
          accessibilityRole="button"
          testID={testID}
          accessibilityState={{ disabled: isDisabled, expanded: false }}
          {...Platform.select({
            web: {
              'aria-label': a11yBase,
              role: 'combobox',
              'aria-expanded': false,
              tabIndex: isDisabled ? -1 : 0,
              onKeyDown: (e: { key?: string; preventDefault?: () => void }) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault?.();
                  handlePress();
                }
              },
            },
          })}
        >
          <View style={styles.webTriggerMain}>
            <View style={styles.triggerTextClip}>{children}</View>
          </View>
          <View style={styles.chevronBlock} accessibilityElementsHidden>
            <ChevronDown {...closedDropdownChevronProps(isDisabled)} />
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
  },
  label: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
    marginBottom: spacing.fieldVerticalGap,
  },
  required: {
    color: colors.error,
  },
  dropdownContainer: {
    position: 'relative',
    zIndex: Platform.OS === 'web' ? 9999 : 10000,
  },
  /** Expandable closed trigger — overrides fixed height/`maxHeight` on {@link styles.dropdown}. */
  dropdownModalTrigger: {
    minHeight: spacing.formControlHeight,
    height: 'auto' as unknown as number,
    maxHeight: 100000,
    alignItems: 'stretch',
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formDropdownBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    backgroundColor: colors.backgroundPrimary,
    minHeight: spacing.formControlHeight,
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        cursor: 'pointer',
        backgroundColor: colors.backgroundForm,
        borderColor: colors.inputBorderNeutral,
        /**
         * Figma Visual Template — Input focused: 2px `--border/hover` (blue). Hover: 1px same token.
         * Focus is on the inner combobox; `:focus-within` on the bordered shell matches the file.
         */
        ':focus-within': {
          outlineWidth: 0,
          borderWidth: 2,
          borderColor: colors.inputBorderInteractive,
          boxShadow: 'none',
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
    }),
  },
  /** Taller closed trigger: primary + subtitle; min height still matches form field. */
  dropdownTwoLine: {
    minHeight: spacing.formControlHeight,
    maxHeight: spacing.formDropdownTriggerTwoLineMaxHeight,
    height: 'auto' as any,
    alignItems: 'center',
    paddingTop: spacing.formDropdownTriggerTwoLinePaddingVertical,
    paddingBottom: spacing.formDropdownTriggerTwoLinePaddingVertical,
    ...Platform.select({
      web: {
        backgroundColor: colors.backgroundForm,
      },
      default: {},
    }),
  },
  /** Second line in closed trigger — matches portaled row subtitle (`8574:33292` / English caption scale). */
  dropdownTriggerSubline: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
    fontWeight: '400',
    marginTop: 2,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  /** Legacy parity: tight trailing chevron (Opportunity `TeamSection` form-field trigger uses `marginLeft: 4` only). */
  chevronBlock: {
    marginLeft: spacing.xs,
    padding: 0,
    alignSelf: 'center',
  },
  /** Web error state: icon inside trigger (message remains below). */
  dropdownTriggerErrorIcon: {
    marginLeft: spacing.xs,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    flexShrink: 0,
  },
  /** Web multiselect closed trigger — chips + “+N”. */
  webMultiChipRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    flexWrap: 'nowrap',
    ...Platform.select({
      web: {
        gap: spacing.sm,
      },
      default: {},
    }),
  },
  webMultiChip: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '46%',
    paddingVertical: spacing.formMultiSelectChipPaddingVertical,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.surfaceMultiSelectChip,
    borderWidth: 1,
    borderColor: colors.borderMultiSelectChip,
    ...Platform.select({
      web: {
        gap: spacing.xs,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  webMultiChipPressed: {
    opacity: 0.85,
  },
  webMultiChipLabel: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    fontWeight: '400',
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
      },
      default: {},
    }),
  },
  webMultiChipOverflow: {
    flexShrink: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    fontWeight: fontWeight.medium,
    color: colors.textSecondary,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
      default: {},
    }),
  },
  /** Fills the bordered trigger; use with `testID="dropdown-trigger"` (includes chevron). */
  webTriggerRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    ...Platform.select({
      web: {
        gap: spacing.sm,
        /**
         * RN Web maps this node to `<button>`. UA `min-height` (e.g. ~44px) otherwise makes the
         * trigger taller than {@link spacing.formControlHeight} text/number inputs in the same row.
         */
        minHeight: 0,
        height: '100%' as unknown as number,
        maxHeight: '100%' as unknown as number,
        cursor: 'pointer' as const,
        userSelect: 'none' as const,
        /**
         * RN Web maps this node to `<button>`. UA + RN default border (~`#ddd` / `borderMedium`)
         * sits on the interactive element, while the design-token stroke is on the outer
         * `dropdown` shell — DevTools on `data-testid` / combobox showed the wrong color.
         */
        borderStyle: 'solid' as const,
        borderWidth: 0,
        borderColor: 'transparent',
        /** Shorthand helps override UA `<button>` borders that ignore per-side zero width in DevTools. */
        border: 'none',
        backgroundColor: 'transparent',
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        /** Native WebKit / Chromium focus ring on `button`/combobox; parent supplies `:focus-within` ring. */
        outlineWidth: 0,
        boxShadow: 'none' as const,
        /**
         * RN Web maps this row to `<button>`. Without an explicit `color`, nested text can pick up a
         * legacy gray (~`#999` / `textMuted`) instead of each `Text` node’s declared style—set body
         * tone here so placeholder labels use {@link styles.placeholderText} / shared exports explicitly.
         */
        color: colors.textPrimary,
      },
    }),
  },
  webTriggerRowReadOnly: {
    ...Platform.select({
      web: {
        cursor: 'default' as const,
        userSelect: 'text' as const,
      },
    }),
  },
  webTriggerMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  nativeDropdownMainTap: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  dropdownClearButton: {
    padding: spacing.xs,
    marginLeft: spacing.subsectionTitleTight - 2,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
    }),
  },
  dropdownClearButtonPressed: {
    opacity: 0.55,
  },
  openDropdown: {
    ...Platform.select({
      web: {
        /** Portaled menu sits below with a gap — keep full 6px radius (Figma Select). */
        borderBottomLeftRadius: spacing.formDropdownBorderRadius,
        borderBottomRightRadius: spacing.formDropdownBorderRadius,
        borderBottomColor: colors.inputBorderInteractive,
      },
      default: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomColor: colors.borderDark,
      },
    }),
  },
  /** Web: menu visible — keep stroke aligned with Figma open select when focus moves into the list. */
  dropdownWebOpen: {
    ...Platform.select({
      web: {
        /** Open trigger keeps **2px** `--border/hover` even when focus moves into the portaled list (`8574:33292`). */
        borderWidth: 2,
        borderColor: colors.inputBorderInteractive,
      },
      default: {},
    }),
  },
  /**
   * Disabled / read-only: align with `FormInput` multiline disabled + Figma tokens — not legacy `#ddd`
   * (`inputBorder`), which read-only `PermissionAwareField` clones looked beside enabled selects.
   */
  disabledDropdown: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    ...Platform.select({
      web: {
        opacity: 1,
        cursor: 'not-allowed' as any,
        backgroundColor: colors.backgroundFormDisabled,
        borderColor: colors.inputBorderDisabled,
        ':hover': {
          borderColor: colors.inputBorderDisabled,
        },
        ':focus-within': {
          borderWidth: 1,
          borderColor: colors.inputBorderDisabled,
          boxShadow: 'none',
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  readOnlyDropdown: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    ...Platform.select({
      web: {
        opacity: 1,
        cursor: 'default' as const,
        backgroundColor: colors.backgroundFormDisabled,
        borderColor: colors.inputBorderDisabled,
        ':hover': {
          borderColor: colors.inputBorderDisabled,
        },
        ':focus-within': {
          borderWidth: 1,
          borderColor: colors.inputBorderDisabled,
          boxShadow: 'none',
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  errorDropdown: {
    borderColor: colors.inputBorderCritical,
    ...Platform.select({
      web: {
        ':focus-within': {
          borderColor: colors.inputBorderCritical,
          boxShadow: 'none',
        },
        ':hover': {
          borderColor: colors.inputBorderCritical,
        },
      },
    }),
  },
  dropdownText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  /** Empty trigger — Visual Template placeholder tone (`colors.inputPlaceholder`), not body muted text. */
  placeholderText: {
    color: colors.inputPlaceholder,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  /** Disabled closed trigger value — template disabled fill uses `--neutral/disabled` text tone. */
  disabledText: {
    color: colors.textFormDisabled,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  readOnlyText: {
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  /** Lets flex children shrink so long values ellipsize instead of growing the row */
  triggerTextClip: {
    flex: 1,
    minWidth: 0,
    ...Platform.select({
      web: {
        cursor: 'inherit' as const,
        userSelect: 'none' as const,
      },
    }),
  },
  triggerTextClipReadOnly: {
    ...Platform.select({
      web: {
        userSelect: 'text' as const,
      },
    }),
  },
  optionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: colors.borderCard,
    borderBottomLeftRadius: spacing.formDropdownBorderRadius,
    borderBottomRightRadius: spacing.formDropdownBorderRadius,
    maxHeight: spacing.formDropdownListMaxHeight,
    shadowColor: colors.shadowBase,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: Platform.OS === 'web' ? 10 : 999,
    zIndex: Platform.OS === 'web' ? 99999 : 999999,
    ...Platform.select({
      web: {
        ...(elevation.popover as ViewStyle),
      },
      default: {
        // Mobile-specific styles to ensure dropdown appears above everything
        overflow: 'visible',
      },
    }),
  },
  optionsList: {
    maxHeight: spacing.formDropdownListMaxHeight,
  },
  option: {
    minHeight: spacing.formDropdownOptionMinHeight,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    paddingVertical: spacing.formDropdownOptionPaddingVertical,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        ':hover': {
          backgroundColor: colors.surfaceHover,
        },
      },
    }),
  },
  /** Selected row — template light blue (`surfaceSelectSelected`). */
  selectedOption: {
    backgroundColor: colors.surfaceSelectSelected,
  },
  optionText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    fontWeight: '400',
  },
  selectedOptionText: {
    color: colors.textPrimary,
    fontWeight: fontWeight.medium,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.xs,
    gap: spacing.formInlineFeedbackGap,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: fontSize.charCount,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  webPortalDropdown: {
    position: 'absolute',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.formDropdownBorderRadius,
    maxHeight: spacing.formDropdownMenuMaxHeight,
    shadowColor: colors.shadowBase,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    zIndex: 999999,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      } as any,
    }),
  },

  // react-native-element-dropdown styles for mobile
  nativeDropdown: {
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formDropdownBorderRadius,
    backgroundColor: colors.backgroundForm,
    minHeight: spacing.formControlHeight,
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    paddingTop: 0,
    paddingBottom: 0,
  },
  /** Overrides fixed height for `textLines={2}` on the native path */
  nativeDropdownTwoLine: {
    minHeight: spacing.formControlHeight,
    maxHeight: spacing.formDropdownTriggerTwoLineMaxHeight,
    height: 'auto' as any,
  },
  nativeDropdownError: {
    borderWidth: 2,
    borderColor: colors.inputBorderCritical,
  },
  nativeDropdownDisabled: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    opacity: 1,
  },
  nativeDropdownReadOnly: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    opacity: 1,
  },
  nativeDropdownContainer: {
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.formDropdownBorderRadius,
    backgroundColor: colors.backgroundPrimary,
    marginTop: spacing.subsectionTitleTight - 2,
    maxHeight: spacing.formDropdownMenuMaxHeight,
    shadowColor: colors.shadowBase,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  nativeDropdownContainerError: {
    borderColor: colors.error,
  },
  nativeDropdownText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
  },
  nativeDropdownTextDisabled: {
    color: colors.textFormDisabled,
  },
  nativeDropdownTextReadOnly: {
    color: colors.textPrimary,
  },
  nativeDropdownPlaceholder: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.inputPlaceholder,
  },

  // Mobile modal dropdown styles
  mobileDropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileDropdownButtonTwoLine: {
    minHeight: spacing.formControlHeight,
  },
  mobileDropdownChevronCell: {
    marginLeft: spacing.sm,
    alignSelf: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalDropdownContainer: {
    position: 'absolute',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.formDropdownBorderRadius,
    maxHeight: spacing.formDropdownMenuMaxHeight,
    shadowColor: colors.shadowBase,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 8,
  },
  modalDropdownScroll: {
    maxHeight: spacing.formDropdownListMaxHeight,
  },
  modalDropdownScrollWithSearch: {
    maxHeight: spacing.formDropdownModalScrollMaxHeightWithSearch,
  },
  /** Padded band above options; `FormSearchField` matches Figma / View Requests outlined search. */
  dropdownMenuSearchShell: {
    backgroundColor: colors.backgroundPrimary,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderDark,
    padding: spacing.formDropdownSearchShellPadding,
  },
  modalDropdownItemInner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  modalDropdownItemInnerMulti: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  multiSelectCheck: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    marginLeft: 'auto' as any,
    marginTop: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  multiSelectCheckOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  modalOptionIconCell: {
    marginRight: spacing.sm,
    marginTop: 1,
  },
  modalDropdownTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  modalDropdownEmpty: {
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    paddingVertical: spacing.formDropdownOptionPaddingVertical,
  },
  modalDropdownEmptyText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
  },
  modalDropdownItem: {
    minHeight: spacing.formDropdownOptionMinHeight,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    paddingVertical: spacing.formDropdownOptionPaddingVertical,
    justifyContent: 'center',
  },
  modalDropdownItemSelected: {
    backgroundColor: colors.surfaceSelectSelected,
  },
  modalDropdownItemText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    fontWeight: '400',
    flexShrink: 1,
  },
  modalDropdownItemTextSelected: {
    color: colors.textPrimary,
    fontWeight: '400',
  },
  modalDropdownItemSubtext: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textSecondary,
    marginTop: 2,
    fontWeight: '400',
  },
});

/**
 * Empty closed-trigger label for custom {@link FormDropdownTriggerShell} children.
 * Explicit {@link colors.inputPlaceholder} (same as {@link FormInput} placeholder / {@link styles.placeholderText}),
 * not inherited UA grey on web `<button>` content.
 */
export const formDropdownTriggerPlaceholderLabelStyle: TextStyle = {
  fontSize: fontSize.formControl,
  lineHeight: lineHeight.formControl,
  fontWeight: '400',
  color: colors.inputPlaceholder,
  ...Platform.select({
    web: {
      cursor: 'inherit' as const,
      userSelect: 'none' as const,
    },
    default: {},
  }),
};
